// File System Access API utility functions and types

interface FileSystemDirectoryHandleWithPermission extends FileSystemDirectoryHandle {
  queryPermission(descriptor: { mode: 'readwrite' }): Promise<PermissionState>;
}

// Sanitize file/directory names by removing invalid characters
export const sanitizeFileName = (name: string): string => {
  if (!name || typeof name !== 'string') {
    return 'untitled';
  }

  // Replace invalid characters with underscores
  // Invalid characters: / \ : * ? " < > |
  // Also handle other problematic characters
  return (
    name
      .replace(/[/\\:*?"<>|]/g, '_')
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/^\.+/, '') // Remove leading dots
      .replace(/\.+$/, '') // Remove trailing dots
      .replace(/\s+$/, '') // Remove trailing spaces
      .trim()
      .substring(0, 255) || // Limit length to 255 characters
    'untitled'
  ); // Fallback if result is empty
};

// Check if the browser supports the File System Access API
export const isFileSystemAccessSupported = (): boolean => {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
};

// Test if a directory handle is still valid and accessible
export const isDirectoryHandleValid = async (
  handle: FileSystemDirectoryHandleWithPermission
): Promise<boolean> => {
  try {
    if (!handle || typeof handle.queryPermission !== 'function') {
      return false;
    }

    const permission = await handle.queryPermission({ mode: 'readwrite' });
    return permission === 'granted';
  } catch (error) {
    console.warn('Directory handle validation failed:', error);
    return false;
  }
};

// Type-safe wrapper for showDirectoryPicker
export const selectDirectory = async (): Promise<FileSystemDirectoryHandle> => {
  if (!isFileSystemAccessSupported()) {
    throw new Error('File System Access API not supported');
  }

  try {
    return await window.showDirectoryPicker({
      mode: 'readwrite',
    });
  } catch (error) {
    throw new Error(`Failed to select directory: ${error}`);
  }
};

// Create a directory handle with proper error handling
export const createDirectory = async (
  parentHandle: FileSystemDirectoryHandle,
  name: string,
  retryCount: number = 0
): Promise<FileSystemDirectoryHandle> => {
  const maxRetries = 3;
  const baseDelay = 500; // 500ms base delay for directories

  try {
    if (
      !parentHandle ||
      typeof parentHandle.getDirectoryHandle !== 'function'
    ) {
      console.error('Invalid parent directory handle:', parentHandle);
      throw new Error('Invalid parent directory handle');
    }

    // Sanitize the directory name before creating
    const sanitizedName = sanitizeFileName(name);

    // Add a small delay to avoid overwhelming the File System API
    if (retryCount > 0) {
      const delay = baseDelay * Math.pow(2, retryCount - 1); // Exponential backoff
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    // Simplified approach - try to create the directory directly
    const dirHandle = await parentHandle.getDirectoryHandle(sanitizedName, {
      create: true,
    });
    return dirHandle;
  } catch (error) {
    console.error(
      `Error creating directory "${name}" (attempt ${retryCount + 1}):`,
      error
    );

    // Retry logic for certain types of errors
    if (retryCount < maxRetries && error instanceof Error) {
      if (
        error.name === 'NotFoundError' ||
        error.name === 'NotAllowedError' ||
        error.message.includes('not found') ||
        error.message.includes('Permission')
      ) {
        return createDirectory(parentHandle, name, retryCount + 1);
      }
    }

    // Provide more specific error messages for final failure
    if (error instanceof Error) {
      if (error.name === 'NotFoundError') {
        throw new Error(
          `Parent directory not found when creating directory "${name}" after ${
            retryCount + 1
          } attempts. The parent handle may have become invalid.`
        );
      } else if (error.name === 'NotAllowedError') {
        throw new Error(
          `Permission denied when creating directory "${name}" after ${
            retryCount + 1
          } attempts`
        );
      } else if (error.message.includes('Invalid directory name')) {
        throw new Error(
          `Invalid directory name "${name}". Please check for special characters.`
        );
      }
    }

    throw new Error(
      `Failed to create directory "${name}" after ${
        retryCount + 1
      } attempts: ${error}`
    );
  }
};

// Create a file and write content with proper error handling
export const createFileWithContent = async (
  directoryHandle: FileSystemDirectoryHandle,
  fileName: string,
  content: Blob,
  retryCount: number = 0
): Promise<void> => {
  const maxRetries = 3;
  const baseDelay = 1000; // 1 second base delay

  try {
    if (
      !directoryHandle ||
      typeof directoryHandle.getFileHandle !== 'function'
    ) {
      console.error('Invalid directory handle:', directoryHandle);
      throw new Error('Invalid directory handle');
    }

    // Sanitize the filename before creating
    const sanitizedFileName = sanitizeFileName(fileName);

    // Add a small delay to avoid overwhelming the File System API
    if (retryCount > 0) {
      const delay = baseDelay * Math.pow(2, retryCount - 1); // Exponential backoff
      await new Promise((resolve) => setTimeout(resolve, delay));
    }

    // Create the file handle
    const fileHandle = await directoryHandle.getFileHandle(sanitizedFileName, {
      create: true,
    });

    // Create a writable stream
    const writable = await fileHandle.createWritable();

    // Write the content
    await writable.write(content);
    await writable.close();
  } catch (error) {
    console.error(
      `Error writing file "${fileName}" (attempt ${retryCount + 1}):`,
      error
    );

    // Retry logic for certain types of errors
    if (retryCount < maxRetries && error instanceof Error) {
      if (
        error.name === 'NotFoundError' ||
        error.name === 'NotAllowedError' ||
        error.message.includes('not found') ||
        error.message.includes('Permission')
      ) {
        return createFileWithContent(
          directoryHandle,
          fileName,
          content,
          retryCount + 1
        );
      }
    }

    throw new Error(
      `Failed to write file "${fileName}" after ${
        retryCount + 1
      } attempts: ${error}`
    );
  }
};

// Create nested directories recursively
export const createNestedDirectories = async (
  rootHandle: FileSystemDirectoryHandle,
  pathParts: string[]
): Promise<FileSystemDirectoryHandle> => {
  let currentHandle = rootHandle;

  for (const part of pathParts) {
    if (part) {
      currentHandle = await createDirectory(currentHandle, part);
    }
  }

  return currentHandle;
};
